import { BaseService } from "../../core/baseService";
import { ApiResult } from "../../core/types";
import {
  ReservationImportServiceInterface,
  ReservationImportJob,
  ReservationImportJobDetail,
  ImportJobsPagedResult,
  ExtractedReservationsPagedResult,
  ExtractedReservation,
  GetImportJobsParams,
  GetExtractedReservationsParams,
  UpdateExtractedReservationParams,
  ApproveReservationParams,
  BulkApproveParams,
  BulkApproveResponse,
} from "../types/reservationImport.types";

const BASE_URL = "/api/admin/reservation-import";

export class ReservationImportService
  extends BaseService
  implements ReservationImportServiceInterface
{
  async uploadFile(file: File): Promise<ApiResult<ReservationImportJob>> {
    const formData = new FormData();
    formData.append("file", file);

    return this.handleRequest<ReservationImportJob>(
      this.client.post(`${BASE_URL}/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
    );
  }

  async submitText(text: string): Promise<ApiResult<ReservationImportJob>> {
    return this.handleRequest<ReservationImportJob>(
      this.client.post(`${BASE_URL}/text`, { text })
    );
  }

  async getJobs(
    params?: GetImportJobsParams
  ): Promise<ApiResult<ImportJobsPagedResult>> {
    const queryParams = this.buildQueryParams({
      pageNumber: params?.pageNumber ?? 1,
      pageSize: params?.pageSize ?? 10,
      status: params?.status,
    });

    return this.handleRequest<ImportJobsPagedResult>(
      this.client.get(`${BASE_URL}/jobs`, { params: queryParams })
    );
  }

  async getJobDetail(
    jobId: string
  ): Promise<ApiResult<ReservationImportJobDetail>> {
    return this.handleRequest<ReservationImportJobDetail>(
      this.client.get(`${BASE_URL}/jobs/${jobId}`)
    );
  }

  async deleteJob(jobId: string): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.delete(`${BASE_URL}/jobs/${jobId}`)
    );
  }

  async retryJob(jobId: string): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.post(`${BASE_URL}/jobs/${jobId}/retry`)
    );
  }

  async getExtractedReservations(
    params: GetExtractedReservationsParams
  ): Promise<ApiResult<ExtractedReservationsPagedResult>> {
    const queryParams = this.buildQueryParams({
      pageNumber: params.pageNumber ?? 1,
      pageSize: params.pageSize ?? 20,
      status: params.status,
      minConfidence: params.minConfidence,
      search: params.search,
    });

    return this.handleRequest<ExtractedReservationsPagedResult>(
      this.client.get(`${BASE_URL}/jobs/${params.jobId}/reservations`, {
        params: queryParams,
      })
    );
  }

  async getExtractedReservation(
    reservationId: string
  ): Promise<ApiResult<ExtractedReservation>> {
    return this.handleRequest<ExtractedReservation>(
      this.client.get(`${BASE_URL}/reservations/${reservationId}`)
    );
  }

  async updateExtractedReservation(
    params: UpdateExtractedReservationParams
  ): Promise<ApiResult<void>> {
    const { reservationId, ...updateData } = params;
    return this.handleRequest<void>(
      this.client.put(`${BASE_URL}/reservations/${reservationId}`, updateData)
    );
  }

  async approveReservation(
    params: ApproveReservationParams
  ): Promise<ApiResult<void>> {
    const { reservationId, ...approveData } = params;
    return this.handleRequest<void>(
      this.client.post(
        `${BASE_URL}/reservations/${reservationId}/approve`,
        approveData
      )
    );
  }

  async rejectReservation(reservationId: string): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.post(`${BASE_URL}/reservations/${reservationId}/reject`)
    );
  }

  async bulkApproveReservations(
    params: BulkApproveParams
  ): Promise<ApiResult<BulkApproveResponse>> {
    const { jobId, ...bulkData } = params;
    return this.handleRequest<BulkApproveResponse>(
      this.client.post(`${BASE_URL}/jobs/${jobId}/bulk-approve`, bulkData)
    );
  }
}
